import React from 'react'
import { useEffect } from 'react'
import { useState } from 'react'
import axios from 'axios'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import { API_BASE_URL } from '../../config'
import { validateLoginForm, FieldError } from '../../utils/formValidation'
import kannariLogo from '../../assets/brand/kannari-logo-gold.png'

const baseUrl = API_BASE_URL

const Login = () => {

    const studentLoginStatus=localStorage.getItem('studentLoginStatus');

    useEffect(() => {
      window.scrollTo(0, 0)
    }, [])
    
    const [studentLoginData,setStudentLoginData]=useState({
        email:'',
        password:''
      });

      const [errorMsg, setErrorMsg]=useState('')
      const [loading, setLoading] = useState(false)
      const [resendingVerification, setResendingVerification] = useState(false)
      const [showResendVerification, setShowResendVerification] = useState(false)
      const [fieldErrors, setFieldErrors] = useState({})

      const handleChange=(event)=>{
        setStudentLoginData({
            ...studentLoginData,
            [event.target.name]:event.target.value
        });
    }

    const submitForm=async()=>{
      if (loading) return

        const errors = validateLoginForm(studentLoginData);
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) return;

        const studentFormData=new FormData;
        studentFormData.append('email',studentLoginData.email)
        studentFormData.append('password',studentLoginData.password)
      setErrorMsg('')
      setShowResendVerification(false)
      setLoading(true)
        try{
        const res = await axios.post(baseUrl+'/student-login',studentFormData)
        if(res.data.bool==true){
          localStorage.setItem('studentLoginStatus',true);
          localStorage.setItem('studentId',res.data.student_id);
          localStorage.setItem('studentActivated', res.data.is_activated ? 'true' : 'false');
          // Pending accounts must enter an activation code before accessing content.
          // Use a truthiness check, not `=== false`: if the API ever omits the field,
          // `undefined === false` is false and a pending student would reach the dashboard.
          window.location.href = res.data.is_activated ? '/student/dashboard' : '/student/activate';
        }else{
          const message = res.data?.message || 'Please enter valid login details.'
          setErrorMsg(message)
          setShowResendVerification(/verify your email/i.test(message))
          Swal.fire({
            title: message,
            icon:'error',
            toast:true,
            timer:2200,
            position:'top',
            timerProgressBar: true,
            showConfirmButton: false
          });
        }
        }catch(error){
            console.log(error)
        const message = error?.response?.data?.message || 'Login failed. Please try again.'
        setErrorMsg(message)
        setShowResendVerification(/verify your email/i.test(message))
        Swal.fire({
          title: message,
          icon:'error',
          toast:true,
          timer:2200,
          position:'top',
          timerProgressBar: true,
          showConfirmButton: false
        });
      } finally {
        setLoading(false)
        }
    }

    const resendVerificationEmail = async () => {
      const email = (studentLoginData.email || '').trim()
      if (!email) {
        Swal.fire({
          title: 'Please enter your email first',
          icon: 'warning',
          toast: true,
          timer: 2200,
          position: 'top',
          timerProgressBar: true,
          showConfirmButton: false
        })
        return
      }

      setResendingVerification(true)
      try {
        const res = await axios.post(baseUrl + '/verify-email/resend/student/', { email })
        const msg = res?.data?.message || 'If this email exists, a verification link has been sent.'
        // An already-verified address gets no email by design — show it as info, not
        // success, otherwise a "sent!" toast with no email arriving looks like a failure.
        const alreadyVerified = !!res?.data?.already_verified
        Swal.fire({
          title: msg,
          icon: alreadyVerified ? 'info' : (res?.data?.bool ? 'success' : 'warning'),
          toast: true,
          timer: alreadyVerified ? 5000 : 3200,
          position: 'top',
          timerProgressBar: true,
          showConfirmButton: false
        })
      } catch (error) {
        const msg = error?.response?.data?.message || 'Could not resend verification email right now.'
        Swal.fire({
          title: msg,
          icon: 'error',
          toast: true,
          timer: 3200,
          position: 'top',
          timerProgressBar: true,
          showConfirmButton: false
        })
      } finally {
        setResendingVerification(false)
      }
    }

    // Arriving from an email-verification link (?verified=1) means the visitor just
    // confirmed a specific account and now needs to sign in AS that account. If this
    // device still holds an older session, the auto-redirect below would silently drop
    // them into that previous account instead — which is exactly what made verification
    // on a second device appear to log into someone else's account. Clear it first.
    const justVerified = new URLSearchParams(window.location.search).get('verified') === '1'
    if (justVerified && studentLoginStatus === 'true') {
        localStorage.removeItem('studentLoginStatus')
        localStorage.removeItem('studentId')
        localStorage.removeItem('studentActivated')
    }

    if(studentLoginStatus=='true' && !justVerified){
        window.location.href='/student/dashboard';
    }

    useEffect(()=>{
        document.title='LMS | Login'
      })

  return (
  <>
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F7F3EA 0%, #e8ecf1 100%)',
      padding: '40px 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{ maxWidth: '1200px', width: '100%' }}>
        
        {/* Header Section */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <img src={kannariLogo} alt="Kannari Music Academy" style={{ height: '64px', width: 'auto', margin: '0 auto 24px', display: 'block' }} />
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            background: '#e3f2fd',
            borderRadius: '20px',
            fontSize: '14px',
            color: '#101C2C',
            fontWeight: '500',
            marginBottom: '20px'
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            Welcome back, Student
          </div>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '700',
            color: '#101C2C',
            marginBottom: '10px',
            letterSpacing: '-0.5px'
          }}>Sign In to Your Account</h1>
          <p style={{
            fontSize: '16px',
            color: '#6b7280',
            fontWeight: '400'
          }}>Continue your learning journey</p>
        </div>

        {/* Main Card */}
        <div style={{ maxWidth: '480px', margin: '0 auto' }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '48px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
          }}>
            
            {/* Back Link */}
            <Link to="/" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              color: '#6b7280',
              textDecoration: 'none',
              fontSize: '14px',
              marginBottom: '32px',
              fontWeight: '500',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.color = '#101C2C'}
            onMouseLeave={(e) => e.target.style.color = '#6b7280'}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Back to home
            </Link>

            {/* Header with Icon */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: 'linear-gradient(135deg, #101C2C 0%, #7C9BB8 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <div>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#101C2C',
                  marginBottom: '4px',
                  letterSpacing: '-0.3px'
                }}>Student Sign In</h2>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: 0
                }}>Enter your credentials to continue</p>
              </div>
            </div>

            {/* Error Message */}
            {justVerified && !errorMsg && (
              <div style={{
                padding: '14px 16px',
                background: '#d1e7dd',
                border: '1px solid #badbcc',
                borderRadius: '8px',
                color: '#0f5132',
                marginBottom: '24px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                ✓ Email verified. Sign in below — you'll then be asked for your
                activation code before your account is unlocked.
              </div>
            )}

            {errorMsg && (
              <div style={{
                padding: '14px 16px',
                background: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '8px',
                color: '#721c24',
                marginBottom: '24px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                ⚠ {errorMsg}
              </div>
            )}

            {showResendVerification && (
              <div style={{
                marginTop: '-8px',
                marginBottom: '16px',
                padding: '12px',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px'
              }}>
                <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#101C2C' }}>
                  Didn't receive the verification email? Click below to resend.
                </p>
                <button
                  type="button"
                  onClick={resendVerificationEmail}
                  disabled={resendingVerification}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: resendingVerification ? '#7C9BB8' : '#101C2C',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: resendingVerification ? 'not-allowed' : 'pointer'
                  }}
                >
                  {resendingVerification ? 'Resending...' : 'Resend Verification Email'}
                </button>
              </div>
            )}

            {/* Form Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Email Address */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>Email Address</label>
                <input
                  type="email"
                  onChange={handleChange}
                  name="email"
                  value={studentLoginData.email}
                  placeholder="Enter your email (e.g., student@kannari.com)"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '15px',
                    border: fieldErrors.email ? '1px solid #D85C4A' : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box',
                    color: '#101C2C'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#101C2C';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = fieldErrors.email ? '#D85C4A' : '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <FieldError error={fieldErrors.email} />
              </div>

              {/* Password */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>Password</label>
                <input
                  type="password"
                  onChange={handleChange}
                  name="password"
                  value={studentLoginData.password}
                  placeholder="Enter your password"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '15px',
                    border: fieldErrors.password ? '1px solid #D85C4A' : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box',
                    color: '#101C2C'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#101C2C';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = fieldErrors.password ? '#D85C4A' : '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <FieldError error={fieldErrors.password} />
                <div style={{ marginTop: '8px', textAlign: 'right' }}>
                  <Link
                    to="/student/forgot-password"
                    style={{ fontSize: '13px', color: '#101C2C', textDecoration: 'none', fontWeight: '500' }}
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={submitForm}
                type="button"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg, #101C2C 0%, #7C9BB8 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: loading ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.4)',
                  opacity: loading ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                  }
                }}
              >
                {loading ? (
                  <>
                    <span style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      display: 'inline-block',
                      animation: 'spin 0.6s linear infinite'
                    }}></span>
                    Logging in...
                    <style>{`
                      @keyframes spin {
                        to { transform: rotate(360deg); }
                      }
                    `}</style>
                  </>
                ) : (
                  <>
                    Sign In
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </>
                )}
              </button>

              {/* Divider */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                margin: '8px 0'
              }}>
                <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
                <span style={{ fontSize: '13px', color: '#9ca3af', fontWeight: '500' }}>OR</span>
                <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
              </div>

              {/* Sign Up Link */}
              <Link
                to="/student/register"
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  background: 'white',
                  color: '#101C2C',
                  border: '2px solid #101C2C',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'center',
                  textDecoration: 'none',
                  display: 'block',
                  boxSizing: 'border-box'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f8f9ff';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Don't have an account? Sign Up
              </Link>

            </div>
          </div>
        </div>
      </div>
    </div>
  </>
  )
}

export default Login